import { sessionDateOf } from './replayStats';

/**
 * WHO TRADED, AND WHICH WAY: solid markers for the engine, hollow ones for the operator.
 *
 * <h2>Why arrows, when the entry and exit verticals already exist</h2>
 *
 * `replayAnnotations` draws a vertical at every entry and exit and a diagonal between them. Those
 * answer WHEN and WHAT IT DID. They do not answer WHICH WAY at a price — a vertical spans the
 * whole grid, so it says nothing about the level, and on a chart carrying two books it says
 * nothing about whose trade it was.
 *
 * <p>An arrow at the fill price does both: it sits at the level the trade happened at, and its
 * fill tells the reader in one glance whether the engine or the operator did it.
 *
 * <h2>SOLID IS THE MACHINE, HOLLOW IS THE HUMAN</h2>
 *
 * <pre>
 *   algo buy    solid   green    arrow up,   below the price
 *   algo sell   solid   red      arrow down, above the price
 *   human buy   hollow  cyan     triangle up,   below the price
 *   human sell  hollow  magenta  triangle down, above the price
 * </pre>
 *
 * Two axes, read independently: the SILHOUETTE says whose book, the COLOUR and DIRECTION say what
 * the order was. Using colour alone for both would mean four shades to learn and a colour-blind
 * reader could not tell the two books apart at all.
 *
 * <h2>The marker is the ORDER, not the outcome</h2>
 *
 * A short's ENTRY is a sell and its EXIT is a buy. So a losing short shows a red down arrow at the
 * entry and a green up arrow at the exit, which is what actually happened — the profit or loss is
 * already said by the exit vertical's colour and by the vector's slope, and a third encoding of it
 * would compete with those while adding nothing.
 *
 * <h2>Nothing after the playhead, on EITHER book</h2>
 *
 * The algo side is filtered by frame index, as every other annotation is. The human side is
 * filtered by DATE, and that is not redundant: the transport steps backwards, so an operator who
 * trades at bar 400 and then presses ArrowLeft would otherwise be looking at their own future
 * fill. The comparison is only honest if both books are truncated at the same bar.
 */

/*
 * THE FOUR PENS COME FROM `simulatorPalette`.
 *
 * Emerald and crimson for the engine, neon cyan and magenta for the operator. Re-exported under
 * the names this module already used, so every importer and every existing assertion about
 * "which column is which" keeps working while the values are declared in one place.
 *
 * They moved: the algo pair was `#2e7d32`/`#c62828`, which it shared with the exit verticals, and
 * the human pair was a slightly duller `#00e5ff`/`#ff4081`. The exit verticals keep their own
 * colours deliberately — a vertical says a trade ENDED and in profit or not, while an arrow says
 * an ORDER was buy or sell, and those are different claims that happened to share a hex.
 */
export {
  ALGO_BUY_COLOR, ALGO_SELL_COLOR, HUMAN_BUY_COLOR, HUMAN_SELL_COLOR,
} from './simulatorPalette';
import {
  ALGO_BUY_COLOR, ALGO_SELL_COLOR, HUMAN_BUY_COLOR, HUMAN_SELL_COLOR,
} from './simulatorPalette';

/**
 * How far off the price each book's marker sits.
 *
 * <p>Two different distances on purpose. A human trade usually happens AT or NEAR an algo trade —
 * that is the whole point of a copilot — so at one offset the two markers would overlap on exactly
 * the bars a reader most wants to compare. The human's sits further out, so a bar carrying both
 * shows both.
 */
export const ALGO_OFFSET_PX = 20;
export const HUMAN_OFFSET_PX = 34;

export const ALGO_SYMBOL_SIZE = 11;
export const HUMAN_SYMBOL_SIZE = 12;

export const LEDGER_MARKER_SERIES_ID = 'dynaman-ledger-markers-series';
export const LEDGER_MARKER_OVERLAY_ID = 'dynaman-ledger-markers';

/**
 * One marker.
 *
 * @param side   'BUY' or 'SELL' — the ORDER, not the trade's direction
 * @param book   'ALGO' or 'HUMAN'
 */
function marker({ date, price, side, book, title }) {
  const buy = side === 'BUY';
  const human = book === 'HUMAN';
  const colour = human
    ? (buy ? HUMAN_BUY_COLOR : HUMAN_SELL_COLOR)
    : (buy ? ALGO_BUY_COLOR : ALGO_SELL_COLOR);
  const offset = (human ? HUMAN_OFFSET_PX : ALGO_OFFSET_PX) * (buy ? 1 : -1);

  return {
    coord: [date, price],
    // `arrow` for the engine and `triangle` for the operator, hollow in the human case. ECharts
    // draws an `emptyTriangle` with the item's colour as its BORDER and the background through
    // the middle, which is what makes "hollow" legible over candles rather than a pale fill.
    symbol: human ? 'emptyTriangle' : 'arrow',
    symbolSize: human ? HUMAN_SYMBOL_SIZE : ALGO_SYMBOL_SIZE,
    // Rotation, not a second symbol: an up arrow rotated 180 is the same glyph the other way up,
    // so the two can never drift into different sizes or weights.
    symbolRotate: buy ? 0 : 180,
    symbolOffset: [0, offset],
    itemStyle: human
      ? { color: 'transparent', borderColor: colour, borderWidth: 2, opacity: 0.95 }
      : { color: colour, opacity: 0.95 },
    label: { show: false },
    // Read by the chart's own hover card. Named so a reader can tell the two books apart on a bar
    // where both traded, which is the one case the silhouette alone can be ambiguous at small zoom.
    name: title,
    book,
    side,
  };
}

/** The ORDER a trade's entry was: buying opens a long, selling opens a short. */
export const entrySideOf = (direction) => (direction === 'SHORT' ? 'SELL' : 'BUY');
export const exitSideOf = (direction) => (direction === 'SHORT' ? 'BUY' : 'SELL');

const onAxis = (dates, date) => !!date && dates.indexOf(date) >= 0;

/**
 * The engine's fills, up to the playhead.
 *
 * <p>Entries AND exits, from `timeline.trades` — the same collection the entry verticals and the
 * vectors read, so a bar can never show one trade's arrow beside another trade's vertical.
 *
 * <p>The OPEN premise contributes its entry only. Its exit has not happened, and an arrow for it
 * would be the future arriving through the chart in the most persuasive possible form: as a mark
 * the engine supposedly made.
 */
export function algoTradeMarks(timeline, dates, upToIndex) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0) return [];
  const stamped = Array.isArray(timeline?.trades) ? timeline.trades : [];
  const out = [];

  stamped.forEach((s) => {
    if (!s || !s.trade) return;
    if (!Number.isInteger(s.frameIndex) || s.frameIndex < 0 || s.frameIndex > upToIndex) return;
    const t = s.trade;
    const entry = sessionDateOf(t.entryTime);
    const exit = sessionDateOf(t.exitTime);
    if (onAxis(axis, entry) && Number.isFinite(t.entryPrice)) {
      out.push(marker({
        date: entry,
        price: t.entryPrice,
        side: entrySideOf(t.direction),
        book: 'ALGO',
        title: `ALGO ${entrySideOf(t.direction)} ${t.entryPrice.toFixed(2)}`,
      }));
    }
    if (onAxis(axis, exit) && Number.isFinite(t.exitPrice)) {
      out.push(marker({
        date: exit,
        price: t.exitPrice,
        side: exitSideOf(t.direction),
        book: 'ALGO',
        title: `ALGO ${exitSideOf(t.direction)} ${t.exitPrice.toFixed(2)}`,
      }));
    }
  });

  // The still-running premise, whose entry is a fact and whose exit is not.
  const open = timeline?.openTrade;
  if (open && Number.isFinite(open.entryPrice)) {
    const date = sessionDateOf(open.entryTime);
    const frames = timeline?.frames || [];
    const reached = frames.findIndex((f) => f && sessionDateOf(f.time) === date);
    if (onAxis(axis, date) && reached >= 0 && reached <= upToIndex) {
      out.push(marker({
        date,
        price: open.entryPrice,
        side: entrySideOf(open.direction),
        book: 'ALGO',
        title: `ALGO ${entrySideOf(open.direction)} ${open.entryPrice.toFixed(2)} (open)`,
      }));
    }
  }

  return out;
}

/**
 * The operator's fills, up to the playhead.
 *
 * <p>Filtered on DATE rather than on an index, because the human ledger has no frames — it is a
 * list of trades stamped with the bar they were taken on. `upToDate` is the playhead's own session
 * date, and a trade taken at a bar the transport has since stepped back from is hidden rather than
 * dropped: the ledger still holds it, and stepping forward again shows it.
 */
export function humanTradeMarks(human, dates, upToDate) {
  const axis = Array.isArray(dates) ? dates : [];
  if (axis.length === 0 || !human) return [];
  const out = [];
  const visible = (date) => onAxis(axis, date) && (!upToDate || date <= upToDate);

  (human.trades || []).forEach((t) => {
    if (!t) return;
    if (visible(t.entryTime) && Number.isFinite(t.entryPrice)) {
      out.push(marker({
        date: t.entryTime,
        price: t.entryPrice,
        side: entrySideOf(t.direction),
        book: 'HUMAN',
        title: `YOU ${entrySideOf(t.direction)} ${t.entryPrice.toFixed(2)}`,
      }));
    }
    if (visible(t.exitTime) && Number.isFinite(t.exitPrice)) {
      out.push(marker({
        date: t.exitTime,
        price: t.exitPrice,
        side: exitSideOf(t.direction),
        book: 'HUMAN',
        title: `YOU ${exitSideOf(t.direction)} ${t.exitPrice.toFixed(2)}`,
      }));
    }
  });

  const open = human.position;
  if (open && visible(open.entryTime) && Number.isFinite(open.entryPrice)) {
    out.push(marker({
      date: open.entryTime,
      price: open.entryPrice,
      side: entrySideOf(open.direction),
      book: 'HUMAN',
      title: `YOU ${entrySideOf(open.direction)} ${open.entryPrice.toFixed(2)} (open)`,
    }));
  }

  return out;
}

/**
 * Both books on ONE series, as ECharts wants it.
 *
 * <p>One series rather than two, for the reason every decoration in this replay is one series: the
 * per-tick update is a merge by `id`, and ECharts merges a series without touching its siblings
 * only if each writer owns its own. Two marker series would be two ids to remember removing, and
 * the human half would outlive a closed replay.
 *
 * <p>`markPoint` is declared even when empty — a merge cannot REMOVE a mark by omitting it, so a
 * run that had markers and then stepped back to bar 0 would keep them frozen on the chart.
 */
export function ledgerMarkerMarks(timeline, human, dates, upToIndex, upToDate) {
  return {
    markPoint: {
      // SILENT, so an arrow never steals the hover from the candle underneath it. The chart's own
      // price tooltip is what a reader is reaching for on a bar; the marker's identity is already
      // in its shape and colour.
      silent: true,
      animation: false,
      data: [
        ...algoTradeMarks(timeline, dates, upToIndex),
        ...humanTradeMarks(human, dates, upToDate),
      ],
    },
  };
}
